package com.iptvplayer.web.ui.screen

import androidx.compose.runtime.*
import com.iptvplayer.shared.data.remote.dto.AdminUserDto
import com.iptvplayer.shared.presentation.viewmodel.AdminViewModel
import com.iptvplayer.web.ui.*
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun AdminScreen(vm: AdminViewModel) {
    val state by vm.state.collectAsState()

    PageContainer {
        Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.SpaceBetween) } }) {
            H2(attrs = { style { property("margin", "0 0 4px") } }) { Text("Admin") }
            GhostButton("Refresh") { vm.load() }
        }
        ErrorText(state.error)

        if (state.isLoading && state.users.isEmpty()) {
            Spinner("Loading admin data…")
            return@PageContainer
        }

        state.reports?.let { r ->
            SectionTitle("Reports")
            Div(attrs = {
                style { display(DisplayStyle.Grid); property("grid-template-columns", "repeat(auto-fit, minmax(150px, 1fr))"); property("gap", "12px") }
            }) {
                StatCard("Users", r.totalUsers.toString())
                StatCard("New (7 days)", r.newUsers7d.toString())
                StatCard("Disabled", r.disabledUsers.toString())
                StatCard("Pending deletions", r.pendingDeletions.toString())
                StatCard("Sources", r.totalSources.toString())
                StatCard("Devices", "${r.onlineDevices}/${r.totalDevices} online")
                StatCard("Favorites", r.totalFavorites.toString())
                StatCard("History", r.totalHistory.toString())
            }
        }

        // Deletion requests first: these are the accounts waiting on review.
        val pending = state.users.filter { it.deletionRequested }
        if (pending.isNotEmpty()) {
            SectionTitle("Deletion requests (${pending.size})")
            pending.forEach { u -> AdminUserRow(u, vm) }
        }

        SectionTitle("Users (${state.users.size})")
        state.users.filter { !it.deletionRequested }.forEach { u -> AdminUserRow(u, vm) }
    }

    state.detail?.let { d -> UserDetailOverlay(d) { vm.closeDetail() } }
}

@Composable
private fun StatCard(label: String, value: String) {
    Div(attrs = { style { cardStyle(); property("padding", "16px") } }) {
        Div(attrs = { style { property("font-size", "26px"); property("font-weight", "800") } }) { Text(value) }
        Div(attrs = { style { property("font-size", "13px"); property("color", Theme.textDim); property("margin-top", "2px") } }) { Text(label) }
    }
}

@Composable
private fun AdminUserRow(u: AdminUserDto, vm: AdminViewModel) {
    Div(attrs = {
        style {
            cardStyle(); display(DisplayStyle.Flex); alignItems(AlignItems.Center); property("gap", "12px")
            property("padding", "12px 14px"); property("margin-bottom", "10px"); property("flex-wrap", "wrap")
            if (u.disabled) property("opacity", "0.6")
        }
    }) {
        Div(attrs = { style { property("flex-grow", "1"); property("min-width", "200px") } }) {
            Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); property("gap", "8px") } }) {
                Span(attrs = { style { property("font-weight", "700") } }) { Text(u.username) }
                if (u.isAdmin) Badge("ADMIN", "#7c3aed")
                if (u.disabled) Badge("DISABLED", Theme.danger)
                if (u.deletionRequested) Badge("DELETION REQUESTED", "#e8590c")
            }
            Div(attrs = { style { property("font-size", "13px"); property("color", Theme.textDim) } }) {
                Text("${u.email} · joined ${fmtDate(u.createdAt)}")
            }
            Div(attrs = { style { property("font-size", "12px"); property("color", Theme.textDim); property("margin-top", "2px") } }) {
                Text("${u.sourceCount} sources · ${u.deviceCount} devices · ${u.favoriteCount} favorites · ${u.historyCount} history")
            }
        }
        Div(attrs = { style { display(DisplayStyle.Flex); property("gap", "8px"); property("flex-wrap", "wrap") } }) {
            GhostButton("Inspect") { vm.openDetail(u.id) }
            GhostButton(if (u.disabled) "Enable" else "Disable") { vm.setDisabled(u.id, !u.disabled) }
            if (u.deletionRequested && !u.isAdmin) {
                GhostButton("Approve deletion") {
                    if (window.confirm("Approve deletion of \"${u.username}\"? This permanently removes the account and ALL its data.")) vm.deleteUser(u.id)
                }
                GhostButton("Reject request") { vm.rejectDeletionRequest(u.id) }
            } else if (!u.isAdmin) {
                GhostButton("Delete") {
                    if (window.confirm("Delete user \"${u.username}\" and ALL their data? This cannot be undone.")) vm.deleteUser(u.id)
                }
            }
        }
    }
}

@Composable
private fun Badge(text: String, color: String) {
    Span(attrs = {
        style {
            property("font-size", "10px"); property("font-weight", "800"); property("letter-spacing", ".5px")
            property("padding", "2px 7px"); property("border-radius", "999px")
            property("background", "${color}22"); property("color", color); property("border", "1px solid ${color}66")
        }
    }) { Text(text) }
}

@Composable
private fun UserDetailOverlay(d: com.iptvplayer.shared.data.remote.dto.AdminUserDetailDto, onClose: () -> Unit) {
    Div(attrs = {
        onClick { onClose() }
        style {
            position(Position.Fixed); top(0.px); left(0.px); right(0.px); bottom(0.px)
            property("background", "rgba(0,0,0,.6)"); property("z-index", "30")
            display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); property("padding", "20px")
        }
    }) {
        Div(attrs = {
            onClick { it.stopPropagation() }
            style {
                cardStyle(); property("background", Theme.surface); property("max-width", "640px"); width(100.percent)
                property("max-height", "85vh"); property("overflow", "auto"); property("padding", "22px")
            }
        }) {
            Div(attrs = { style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center) } }) {
                H3(attrs = { style { property("margin", "0") } }) { Text(d.user.username) }
                GhostButton("Close") { onClose() }
            }
            Div(attrs = { style { property("color", Theme.textDim); property("font-size", "13px"); property("margin-bottom", "10px") } }) {
                Text("${d.user.email} · joined ${fmtDate(d.user.createdAt)}")
            }
            DetailList("Sources (${d.sources.size})", d.sources.map { "${it.name}  ·  ${it.type}" })
            DetailList("Devices (${d.devices.size})", d.devices.map { "${it.name}  ·  ${it.platform}" })
            DetailList("Favorites (${d.favorites.size})", d.favorites.map { it.contentName })
            DetailList("Watch history (${d.history.size})", d.history.map { it.contentName })
        }
    }
}

@Composable
private fun DetailList(title: String, items: List<String>) {
    Div(attrs = { style { property("margin-top", "14px") } }) {
        Div(attrs = { style { property("font-weight", "700"); property("font-size", "14px"); property("margin-bottom", "6px") } }) { Text(title) }
        if (items.isEmpty()) {
            Div(attrs = { style { property("color", Theme.textDim); property("font-size", "13px") } }) { Text("—") }
        } else {
            items.take(50).forEach { line ->
                Div(attrs = {
                    style { property("font-size", "13px"); property("padding", "5px 0"); property("border-bottom", "1px solid ${Theme.border}"); ellipsis() }
                }) { Text(line) }
            }
        }
    }
}

private fun fmtDate(ms: Long): String =
    kotlin.js.Date(ms.toDouble()).toLocaleDateString()
